import React, { useState, useEffect, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { dashboardApi } from '../api/dashboardApi';
import { widgetsApi }   from '../api/widgetsApi';
import type { DashboardLayout, WidgetInstance, WidgetType } from '../types/contracts';
import WidgetCard from '../widgets/WidgetCard';
import Spinner    from '../components/Spinner';
import { haptic } from '../telegram';

const RGL = WidthProvider(Responsive);

const DEFAULT_WIDGETS: WidgetInstance[] = [
  { id: 'w1', type: 'task_counter',       x: 0, y: 0, w: 2, h: 2 },
  { id: 'w2', type: 'agent_runs_status',  x: 2, y: 0, w: 2, h: 2 },
  { id: 'w3', type: 'usage_chart',        x: 0, y: 2, w: 4, h: 3 },
  { id: 'w4', type: 'upcoming_reminders', x: 0, y: 5, w: 2, h: 3 },
  { id: 'w5', type: 'cost_counter',       x: 2, y: 5, w: 2, h: 3 },
];

const WIDGET_LABEL: Record<WidgetType, string> = {
  task_counter:       'Задачи',
  usage_chart:        'Использ.',
  upcoming_reminders: 'Напомин.',
  agent_runs_status:  'Агенты',
  cost_counter:       'Расходы',
};

const headerSt: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
  background: 'var(--tg-theme-header-bg-color)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const h1St: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600 };
const pageSt: React.CSSProperties = { minHeight: '100vh' };
const centeredSt: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 };

export default function Dashboard() {
  const [layout,     setLayout]     = useState<DashboardLayout | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(false);
  const [widgetData, setWidgetData] = useState<Record<string, unknown>>({});

  const fetchLayout = useCallback(async () => {
    setLoading(true);
    try {
      setLayout(await dashboardApi.getLayout());
    } catch {
      setLayout({ userId: 0, widgets: DEFAULT_WIDGETS });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLayout(); }, [fetchLayout]);

  useEffect(() => {
    if (!layout) return;
    layout.widgets.forEach(async (wi) => {
      try {
        const r = await widgetsApi.getData({ type: wi.type, config: wi.config });
        setWidgetData((p) => ({ ...p, [wi.id]: r.data }));
      } catch { /* ignore per-widget errors */ }
    });
  }, [layout]);

  const onLayoutChange = useCallback(async (rglLayout: Layout[]) => {
    if (!layout) return;
    const updated: WidgetInstance[] = layout.widgets.map((wi) => {
      const pos = rglLayout.find((l) => l.i === wi.id);
      return pos ? { ...wi, x: pos.x, y: pos.y, w: pos.w, h: pos.h } : wi;
    });
    const next: DashboardLayout = { ...layout, widgets: updated };
    setLayout(next);
    dashboardApi.putLayout(next).catch(() => {});
  }, [layout]);

  const addWidget = (type: WidgetType) => {
    if (!layout) return;
    const id = 'w' + Date.now();
    const next: DashboardLayout = {
      ...layout,
      widgets: [...layout.widgets, { id, type, x: 0, y: Infinity, w: 2, h: 2 }],
    };
    setLayout(next);
    dashboardApi.putLayout(next).catch(() => {});
    haptic('medium');
  };

  const removeWidget = (id: string) => {
    if (!layout) return;
    const next: DashboardLayout = {
      ...layout,
      widgets: layout.widgets.filter((wi) => wi.id !== id),
    };
    setLayout(next);
    dashboardApi.putLayout(next).catch(() => {});
    haptic('medium');
  };

  const editBtnSt: React.CSSProperties = {
    background: editing
      ? 'var(--tg-theme-button-color)'
      : 'rgba(255,255,255,0.08)',
    color: editing
      ? 'var(--tg-theme-button-text-color)'
      : 'var(--tg-theme-text-color)',
    border: 'none', borderRadius: 10, padding: '6px 14px',
    fontSize: 13, cursor: 'pointer',
  };
  const addBarSt: React.CSSProperties = {
    display: 'flex', gap: 8, padding: '8px 16px', overflowX: 'auto',
  };
  const addBtnSt: React.CSSProperties = {
    flexShrink: 0, fontSize: 12, padding: '6px 12px',
    borderRadius: 20, border: '1px solid rgba(255,255,255,0.18)',
    background: 'transparent', color: 'var(--tg-theme-text-color)',
    cursor: 'pointer', whiteSpace: 'nowrap',
  };

  if (loading) return <div style={centeredSt}><Spinner size={32} /></div>;

  const widgets = layout?.widgets ?? DEFAULT_WIDGETS;
  const rglLayouts = { lg: widgets.map((wi) => ({ i: wi.id, x: wi.x, y: wi.y, w: wi.w, h: wi.h, minW: 2, minH: 2 })) };
  const breakpoints = { lg: 480, md: 360, sm: 0 };
  const cols = { lg: 4, md: 4, sm: 2 };

  return (
    <div style={pageSt}>
      <div style={headerSt}>
        <h1 style={h1St}>Дашборд</h1>
        <button style={editBtnSt} onClick={() => { setEditing((e) => !e); haptic('light'); }}>
          {editing ? 'Готово' : 'Редакт.'}
        </button>
      </div>

      {editing && (
        <div style={addBarSt}>
          {(Object.keys(WIDGET_LABEL) as WidgetType[]).map((type) => (
            <button key={type} style={addBtnSt} onClick={() => addWidget(type)}>
              + {WIDGET_LABEL[type]}
            </button>
          ))}
        </div>
      )}

      <RGL
        layouts={rglLayouts}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={80}
        margin={[12, 12]}
        containerPadding={[12, 12]}
        isDraggable={editing}
        isResizable={editing}
        onLayoutChange={onLayoutChange}
        draggableHandle=".drag-handle"
      >
        {widgets.map((wi) => (
          <div key={wi.id}>
            <WidgetCard
              widget={wi}
              data={widgetData[wi.id]}
              editing={editing}
              onRemove={() => removeWidget(wi.id)}
            />
          </div>
        ))}
      </RGL>
    </div>
  );
}
